import { useEffect, useRef, useState } from 'react';
import Quill from 'quill';

const TOOLBAR = [
  [{ header: [1, 2, 3, false] }],
  ['bold', 'italic', 'underline'],
  [{ color: [] }, { background: [] }],
  [{ align: [] }],
  [{ list: 'ordered' }, { list: 'bullet' }],
  ['link', 'clean'],
];

/** Thin wrapper over Quill 2.x (no react-quill dependency). */
export function QuillEditor({
  value,
  onChange,
  source = false,
}: {
  value: string;
  onChange: (html: string) => void;
  /** Offer a Visual / HTML toggle. Content with a <table> is edited as HTML only: Quill would strip it. */
  source?: boolean;
}) {
  const hasTable = /<table[\s>]/i.test(value ?? '');
  const [htmlMode, setHtmlMode] = useState(false);
  const showHtml = source && (htmlMode || hasTable);
  const holderRef = useRef<HTMLDivElement>(null);
  const quillRef = useRef<Quill | null>(null);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  useEffect(() => {
    if (!holderRef.current || quillRef.current) return;
    const q = new Quill(holderRef.current, {
      theme: 'snow',
      modules: { toolbar: TOOLBAR },
    });
    quillRef.current = q;
    q.clipboard.dangerouslyPasteHTML(value ?? '');
    q.on('text-change', () => {
      onChangeRef.current(q.root.innerHTML);
    });
  }, []);

  // Keep the editor in sync when the value is replaced externally (e.g. AI translate).
  useEffect(() => {
    const q = quillRef.current;
    if (q && value !== q.root.innerHTML) {
      q.clipboard.dangerouslyPasteHTML(value ?? '');
    }
  }, [value]);

  return (
    <div className="quill-editor">
      {source && (
        <div className="btn-group btn-group-sm mb-1" role="group" aria-label="Editor mode">
          <button type="button" className={`btn btn-outline-secondary ${!showHtml ? 'active' : ''}`}
            disabled={hasTable} title={hasTable ? 'This content has a table; edit it as HTML' : undefined}
            onClick={() => setHtmlMode(false)}>Visual</button>
          <button type="button" className={`btn btn-outline-secondary ${showHtml ? 'active' : ''}`}
            onClick={() => setHtmlMode(true)}>HTML</button>
        </div>
      )}
      <div style={{ display: showHtml ? 'none' : undefined }}>
        <div ref={holderRef} style={{ minHeight: 140 }} />
      </div>
      {showHtml && (
        <textarea className="form-control font-monospace" rows={18} value={value ?? ''}
          onChange={(e) => onChange(e.target.value)} spellCheck={false} />
      )}
    </div>
  );
}
